import React, { useMemo, useState } from "react";
import { Flex, Stack, Button, Text, Icon, useColorModeValue } from "@chakra-ui/react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import SubscriptionsHeader from "./SubscriptionsHeader";
import SubscriptionsFilters from "./SubscriptionsFilters";
import SubscriptionsListTable from "./SubscriptionsListTable";
import CreateGlobalSubscriptionDialog from "./CreateGlobalSubscriptionDialog";
import SubscriptionDetailDrawer from "../../components/Main/auditorAndArtiste/subscriptions/SubscriptionDetailDrawer";
import useSubscriptionsList from "./hooks/useSubscriptionsList";

/**
 * Returns the initial filters object used when the page mounts.
 * @returns {Object}
 */
function buildInitialFilters() {
    return { status: "", owner_type: "", subscription_type: "", payment_provider: "", page: 1 };
}

/**
 * Renders prev/next pagination with a "Page X / Y" indicator.
 * Returns null when there is a single page or less.
 * @param {Object} props - { total, limit, page, onChange }
 * @returns {React.ReactElement|null}
 */
function PaginationRow({ total, limit, page, onChange }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const count = Math.max(1, Math.ceil((total || 0) / (limit || 1)));
    if (count <= 1) return null;
    return (
        <Flex justify="center" align="center" gap="12px" py="8px">
            <Button size="sm" variant="outline" leftIcon={<Icon as={MdChevronLeft} />}
                isDisabled={page <= 1} onClick={() => onChange(page - 1)}>Précédent</Button>
            <Text color={labelColor} fontSize="0.8rem" fontWeight="600">Page {page} / {count}</Text>
            <Button size="sm" variant="outline" rightIcon={<Icon as={MdChevronRight} />}
                isDisabled={page >= count} onClick={() => onChange(page + 1)}>Suivant</Button>
        </Flex>
    );
}

/**
 * Admin page for global subscriptions management.
 * Lists all subscriptions across owners, supports filtering, pagination,
 * detail drawer (cancel/reactivate/invoices/failures) and creation.
 * @returns {React.ReactElement}
 */
function Subscriptions() {
    const [filters, setFilters] = useState(buildInitialFilters);
    const [selectedId, setSelectedId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const { items, total, page, limit, loading, error, refresh } = useSubscriptionsList(filters);

    const headerTotal = useMemo(() => (loading && !items.length ? null : total), [loading, items, total]);
    const handlePageChange = (nextPage) => setFilters({ ...filters, page: nextPage });

    return (
        <Stack spacing="16px" p={{ base: "12px", md: "20px" }}>
            <SubscriptionsHeader total={headerTotal} onCreateClick={() => setCreateOpen(true)} />
            <SubscriptionsFilters filters={filters} onChange={setFilters} />
            <SubscriptionsListTable items={items} loading={loading} error={error} onSelect={setSelectedId} />
            <PaginationRow total={total} limit={limit} page={page} onChange={handlePageChange} />
            <SubscriptionDetailDrawer subscriptionId={selectedId}
                onClose={() => setSelectedId(null)} onChanged={refresh} />
            <CreateGlobalSubscriptionDialog open={createOpen}
                onClose={() => setCreateOpen(false)} onCreated={refresh} />
        </Stack>
    );
}

export default Subscriptions;
